<template>
  <v-navigation-drawer
    fixed
    right
    temporary
    width="574"
    class="px-6"
    :value="visible"
    @input="checkInputValue"
  >
    <div class="d-flex pt-6 pb-4">
      <v-btn
        rounded
        fab
        x-small
        class="blue-grey lighten-3 mr-4"
        @click="checkInputValue()"
        icon
      >
        <v-icon color="blue-grey" data-testid="reportsDrawer-button-closeDrawer"
          >mdi-chevron-left</v-icon
        >
      </v-btn>
      <p class="pr-4 ma-0 h6 font-size-18">
        {{ $t('insights.report_list') }}
      </p>
    </div>

    <Alert
      v-if="isReportInfoAlertShown && reports.length > 0"
      data-testid="reportsDrawer-button-closeDrawer"
      dismissable
      :vuetifyProps="{
        color: 'blue',
        icon: 'mdi-information-outline',
      }"
      :buttonPlain="false"
      :text="$t('insights.you_will_receive_a_notification')"
      @dismiss="$emit('closeAlert')"
    />
    <Alert
      class="alert-container text-caption pa-4 px-4 py-5 mb-4"
      :vuetifyProps="{
        color: 'blue',
        icon: 'mdi-information-outline',
      }"
      :buttonPlain="false"
      :text="$t('insights.these_reports_will_not_be_save_pemanently')"
    />
    <ReportsList
      :organizationId="organizationId"
      :reports="reports"
      :reportsLoading="reportsLoading"
      :reportId="reportId"
      @showClosableAlert="isReportInfoAlertShown = true"
      @listGeneratedReports="listGeneratedReports"
      @deleteReport="deleteReport"
      @downloadXlsxReport="downloadXlsxReport"
    />
  </v-navigation-drawer>
</template>

<script>
import ReportsList from './ReportsList.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Alert from '@/components/Alert.vue'
import api from '@/api'
import { mapActions } from 'vuex'

const FIFTEEN_SECONDS = 15000

export default {
  name: 'ReportsDrawer',

  mixins: [mixin, trackerMixin],

  components: {
    ReportsList,
    Alert,
  },

  props: {
    visible: Boolean,
    organizationId: {
      type: String,
      required: false,
    },
    reportId: {
      type: String,
      required: false,
    },
    isReportInfoAlertShown: Boolean,
  },

  data() {
    return {
      reports: [],
      reportsLoading: false,
      listRefresherInterval: null,
    }
  },

  async mounted() {
    await this.listGeneratedReports()
  },

  beforeDestroy() {
    if (this.listRefresherInterval) clearInterval(this.listRefresherInterval)
  },

  computed: {
    isAnyReportInProgress() {
      return this.reports?.some?.(report => report.status === 'inProgress')
    },
  },

  watch: {
    isAnyReportInProgress(newValue) {
      if (newValue) {
        this.listRefresherInterval = setInterval(
          this.silentlyUpdateReportList,
          FIFTEEN_SECONDS
        )

        return
      }

      if (this.listRefresherInterval) {
        this.silentlyUpdateReportList()
        clearInterval(this.listRefresherInterval)
      }
    },
  },

  methods: {
    ...mapActions({
      downloadReport: 'report/downloadReport',
    }),

    checkInputValue(val) {
      if (!val) this.$emit('close')
    },

    async listGeneratedReports() {
      try {
        this.reportsLoading = true
        const response = await api.report.getReportsHistory(this.organizationId)
        this.reports = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.reportsLoading = false
      }
    },

    async deleteReport(id) {
      try {
        await api.report.deleteReportsHistoryEntry(this.organizationId, id)
        this.reports = this.reports.filter(report => report._id !== id)
        this.trackRemoveReport({ id })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async silentlyUpdateReportList() {
      const response = await api.report.getReportsHistory(this.organizationId)
      this.reports = response.data
    },

    async downloadXlsxReport(reportToDownload) {
      try {
        const response = await this.downloadReport({
          reportKey: reportToDownload._id,
          type: reportToDownload.reportType,
        })

        if (response.status === 'success') {
          this.reports = this.reports.filter(
            report => report._id !== reportToDownload._id
          )
          this.trackDownloadReport({
            status: 'completed',
            id: reportToDownload._id,
          })
        }
      } catch (err) {
        this.handleRequestError(err)
        this.trackDownloadReport({ status: 'error', id: reportToDownload._id })
      }
    },
  },
}
</script>
